import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { GuidePosition } from '@/lib/guides'
import type { Heading, Post, PostSummary } from '@/types'

import Link from 'next/link'
import { ArrowRight, ChevronRight, ShieldCheck } from 'lucide-react'

import { Seo } from '@/components/generals/seo'
import { JsonLd } from '@/components/generals/json-ld'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { Prose } from '@/components/blog/prose'
import { Faq } from '@/components/blog/faq'
import { Sources } from '@/components/blog/sources'
import { PostCard } from '@/components/blog/post-card'
import { HubBadge } from '@/components/blog/hub-badge'
import { PostMeta } from '@/components/blog/post-meta'
import { GuideNav } from '@/components/blog/guide-nav'
import { Newsletter } from '@/components/blog/newsletter'
import { BrandMark } from '@/components/generals/brand'
import { AnswerBlock } from '@/components/blog/answer-block'
import { KeyTakeaways } from '@/components/blog/key-takeaways'
import { TableOfContents } from '@/components/blog/table-of-contents'

import { withHeadingIds } from '@/lib/toc'
import { siteConfig } from '@/constants/site'
import { getHubName } from '@/constants/hubs'
import { getGuidePosition } from '@/lib/guides'
import { resolveInternalLinks } from '@/lib/internal-links'
import { getLiveSlugs, getPostBySlug, getRelatedPosts } from '@/lib/posts'

import {
    faqNode,
    buildGraph,
    articleNode,
    breadcrumbNode
} from '@/lib/structured-data'

interface ArticleProps {
    post: Post
    related: PostSummary[]
    headings: Heading[]
    body: string
    position: GuidePosition | null
}

const ArticlePage: FC<ArticleProps> = ({ post, related, headings, body, position }): ReactNode => {
    const hubName = getHubName(post.hub)

    const crumbs = [
        { name: 'Home', path: '/' },
        ...(post.guideSlug && post.guideTitle
            ? [{ name: post.guideTitle, path: `/guides/${post.guideSlug}` }]
            : []),
        { name: hubName, path: `/${post.hub}` },
        { name: post.title, path: `/${post.hub}/${post.slug}` }
    ]

    const faq = faqNode(post.faq, `/${post.hub}/${post.slug}`)

    return (
        <Layout>
            <Seo
                title={post.title}
                description={post.excerpt}
                ogImage={post.coverImage ?? undefined}
                ogImageAlt={post.coverImageAlt ?? post.title}
                ogType='article'
                canonical={`/${post.hub}/${post.slug}`}
                publishedTime={post.publishedAt}
                modifiedTime={post.updatedAt || post.publishedAt}
                author={siteConfig.name}
                section={hubName}
                tags={post.tags}
            />
            <JsonLd
                data={buildGraph(
                    [breadcrumbNode(crumbs), articleNode(post, hubName), faq].filter(
                        Boolean
                    ) as Record<string, unknown>[]
                )}
            />

            <article>
                <Container className='pt-10 sm:pt-14'>
                    <div className='mx-auto max-w-3xl'>
                        <nav
                            aria-label='Breadcrumb'
                            className='flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground'
                        >
                            <Link href='/' className='transition-colors hover:text-foreground'>
                                Home
                            </Link>
                            <ChevronRight className='size-3.5' />
                            {post.guideSlug && post.guideTitle && (
                                <>
                                    <Link
                                        href={`/guides/${post.guideSlug}`}
                                        className='transition-colors hover:text-foreground'
                                        data-mdlfy-cms={`guides:${post.guideId}:title`}
                                    >
                                        {post.guideTitle}
                                    </Link>
                                    <ChevronRight className='size-3.5' />
                                </>
                            )}
                            <Link href={`/${post.hub}`} className='transition-colors hover:text-foreground'>
                                {hubName}
                            </Link>
                        </nav>

                        <div className='mt-6'>
                            <HubBadge hub={post.hub} />
                        </div>

                        <h1
                            className='mt-4 text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-[2.75rem] lg:leading-[1.1]'
                            data-mdlfy-cms={`posts:${post.id}:title`}
                        >
                            {post.title}
                        </h1>

                        <p
                            className='mt-4 text-lg leading-relaxed text-muted-foreground'
                            data-mdlfy-cms={`posts:${post.id}:excerpt`}
                        >
                            {post.excerpt}
                        </p>

                        <PostMeta
                            postId={post.id}
                            authorName={post.authorName}
                            publishedAt={post.publishedAt}
                            updatedAt={post.updatedAt}
                            readMinutes={post.readMinutes}
                            className='mt-6'
                        />
                    </div>
                </Container>

                {post.coverImage && (
                    <Container className='mt-8'>
                        <div className='mx-auto max-w-4xl'>
                            <div className='relative aspect-[16/9] overflow-hidden rounded-2xl border border-border bg-muted'>
                                <img
                                    src={post.coverImage}
                                    alt={post.coverImageAlt ?? post.title}
                                    className='h-full w-full object-cover'
                                />
                            </div>
                        </div>
                    </Container>
                )}

                <Container className='mt-10 pb-6'>
                    <div className='grid gap-10 lg:grid-cols-[minmax(0,1fr)_16rem] lg:gap-14'>
                        <div className='min-w-0 max-w-3xl'>
                            <AnswerBlock answer={post.answer} hub={post.hub} table='posts' rowId={post.id} />

                            <KeyTakeaways items={post.takeaways} hub={post.hub} className='mt-8' />

                            <TableOfContents headings={headings} className='mt-8 lg:hidden' />

                            <Prose html={body} className='mt-8' />

                            <Faq items={post.faq} className='mt-14' />

                            <Sources items={post.sources} className='mt-14' />

                            {post.guideSlug && post.guideTitle && position && (
                                <GuideNav
                                    guideSlug={post.guideSlug}
                                    guideTitle={post.guideTitle}
                                    guideId={post.guideId}
                                    position={position}
                                    className='mt-12'
                                />
                            )}

                        {post.tags.length > 0 && (
                            <div className='mt-10 flex flex-wrap gap-2'>
                                {post.tags.map(tag => (
                                    <span
                                        key={tag}
                                        className='rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground'
                                    >
                                        #{tag}
                                    </span>
                                ))}
                            </div>
                        )}

                        <div className='mt-10 flex items-start gap-3 rounded-xl border border-border bg-secondary/40 p-5'>
                            <ShieldCheck className='mt-0.5 size-5 shrink-0 text-primary' />
                            <p className='text-sm leading-relaxed text-muted-foreground'>
                                This article is for general education and is not a substitute for professional
                                medical advice. Speak with a qualified healthcare provider about your individual
                                circumstances.
                            </p>
                        </div>

                        <div className='mt-10 flex flex-col gap-4 rounded-xl border border-border bg-card p-5 sm:flex-row sm:items-start'>
                            <div className='flex size-14 shrink-0 items-center justify-center rounded-full bg-secondary'>
                                <BrandMark className='size-8' />
                            </div>
                            <div>
                                <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                                    About HealthPathCore
                                </p>
                                <p className='mt-1 font-semibold'>An independent health knowledge library</p>
                                <p className='mt-2 text-sm leading-relaxed text-muted-foreground'>
                                    Every article here is built from published research and guidance issued by
                                    named health bodies, written in plain language, and dated so you can see how
                                    current it is. When the evidence moves, we come back and correct the piece.
                                </p>

                                <Link
                                    href='/methodology'
                                    className='mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline'
                                >
                                    How we research and write
                                    <ArrowRight className='size-4' />
                                </Link>
                            </div>
                        </div>
                        </div>

                        <aside className='hidden lg:block'>
                            <div className='sticky top-24'>
                                <TableOfContents headings={headings} />
                            </div>
                        </aside>
                    </div>
                </Container>
            </article>

            {related.length > 0 && (
                <section className='mt-6 border-t border-border bg-secondary/30 py-14'>
                    <Container>
                        <h2 className='text-2xl font-bold tracking-tight'>More in {hubName}</h2>

                        <div className='mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
                            {related.map(item => (
                                <PostCard key={item.slug} post={item} />
                            ))}
                        </div>

                        <div className='mt-8'>
                            <Link
                                href={`/${post.hub}`}
                                className='inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline'
                            >
                                View all {hubName} articles
                                <ArrowRight className='size-4' />
                            </Link>
                        </div>
                    </Container>
                </section>
            )}

            <section className='py-16'>
                <Container>
                    <Newsletter />
                </Container>
            </section>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<ArticleProps> = async ({ params }) => {
    const hub = params?.hub
    const slug = params?.slug

    if (typeof hub !== 'string' || typeof slug !== 'string') return { notFound: true }

    const post = await getPostBySlug(slug)

    if (!post || post.hub !== hub) return { notFound: true }

    const [related, liveSlugs] = await Promise.all([
        getRelatedPosts(post.hub, post.slug, 3, post.guideId),
        getLiveSlugs()
    ])

    const { html, headings } = withHeadingIds(post.content)

    // Links to articles that have not reached their publish date degrade to
    // plain text, and become links again on the day they go live.
    const { html: body } = resolveInternalLinks(html, new Set(liveSlugs))

    // Chapter position is resolved from the ordered cluster, so an article that
    // is not assigned to a guide simply renders without the nav strip.
    const position = post.guideId ? await getGuidePosition(post.guideId, post.slug) : null

    return { props: { post, related, headings, body, position } }
}

export default ArticlePage
